import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/shared/components/ui/Input';
import type { Filterset } from '@/app/services/filtersetService';
import { createFiltersetService } from '@/app/services/filtersetService';
import { useApiClient } from '@/shared/hooks/useApiClient';

interface FiltersetNameFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  placeholder: string;
  dataTestId: string;
  targetModel?: string | undefined;
  excludeFiltersetId?: number | undefined;
  /**
   * On edit, the original name of the filterset being edited. When provided
   * AND the current value matches it (case-insensitive), the client-side
   * "Has already been taken" check is suppressed — the user is just resaving
   * under the same name (e.g. expression-only edit). The server-side
   * uniqueness validator (legacy `user_filter.rb#ModelValidationSupport::Unique`)
   * still runs with proper `target_model + user + NOT id` scoping so genuine
   * conflicts are surfaced via FiltersetNameExistsError. Closes QA #48 follow-up
   * where stale dev-data duplicates falsely tripped the client check.
   */
  originalName?: string | undefined;
  autoFocusOnMount?: boolean;
  onExactMatchChange?: ((filterset: Filterset | null) => void) | undefined;
}

export const FiltersetNameField: React.FC<FiltersetNameFieldProps> = ({
  id,
  value,
  onChange,
  error,
  placeholder,
  dataTestId,
  targetModel,
  excludeFiltersetId,
  originalName,
  autoFocusOnMount = false,
  onExactMatchChange,
}) => {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const filtersetService = useMemo(() => createFiltersetService(apiClient), [apiClient]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchResults, setSearchResults] = useState<Filterset[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const trimmedValue = value.trim();
  const normalizedSearchTerm = trimmedValue.toLowerCase();

  const filteredResults = useMemo(
    () => searchResults.filter((filter) => filter.id !== excludeFiltersetId),
    [excludeFiltersetId, searchResults],
  );

  const exactMatch = useMemo(() => {
    if (!normalizedSearchTerm) {
      return null;
    }

    // On edit: if the user hasn't changed the name from the original record,
    // suppress the duplicate-name flag. Stale duplicates that pre-date the
    // uniqueness rule should not block a same-name re-save (e.g. expression-only
    // edit). Server-side validator still enforces the legacy scope on submit.
    if (originalName && normalizedSearchTerm === originalName.trim().toLowerCase()) {
      return null;
    }

    return (
      filteredResults.find((filter) => filter.name.trim().toLowerCase() === normalizedSearchTerm) ?? null
    );
  }, [filteredResults, normalizedSearchTerm, originalName]);

  const suggestionResults = useMemo(
    () => filteredResults.filter((filter) => filter.name.trim().toLowerCase() !== normalizedSearchTerm),
    [filteredResults, normalizedSearchTerm],
  );

  useEffect(() => {
    onExactMatchChange?.(exactMatch);
  }, [exactMatch, onExactMatchChange]);

  useEffect(() => {
    if (!autoFocusOnMount) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
      setIsActive(true);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [autoFocusOnMount]);

  useEffect(() => {
    if (!targetModel || trimmedValue.length === 0) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setIsSearching(true);

      try {
        const results = await filtersetService.searchFiltersetsByName({
          targetModel,
          searchTerm: trimmedValue,
        });
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [filtersetService, targetModel, trimmedValue]);

  return (
    <div className="relative">
      <Input
        id={id}
        ref={inputRef}
        value={value}
        onChange={(event) => {
          setHasTyped(true);
          onChange(event.target.value);
        }}
        onFocus={() => setIsActive(true)}
        onBlur={() => {
          window.setTimeout(() => {
            setIsActive(false);
            setHasTyped(false);
          }, 120);
        }}
        placeholder={placeholder}
        className={error ? 'border-error' : ''}
        data-testid={dataTestId}
        autoComplete="off"
        title={
          trimmedValue
            ? exactMatch
              ? t('shared.saveFilterset.existingNameHint', 'This matches an existing saved filter name.')
              : t('shared.saveFilterset.newNameHint', 'This will be saved as a new filter name.')
            : undefined
        }
      />
      {error && <p className="text-sm text-error mt-1">{error}</p>}

      {isActive && hasTyped && trimmedValue.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 border border-border rounded-md bg-surface shadow-lg max-h-52 overflow-y-auto">
          <div className="flex items-center gap-2 px-3 py-2 text-xs text-text-muted/80 border-b border-border">
            <Search className="w-3.5 h-3.5" />
            <span>{t('shared.saveFilterset.matchingNames', 'Matching saved filter names')}</span>
          </div>
          {isSearching ? (
            <div className="px-3 py-2 text-sm text-text-muted/80">{t('common.loading', 'Loading...')}</div>
          ) : suggestionResults.length > 0 ? (
            <ul className="py-1">
              {suggestionResults.map((filter) => (
                <li key={filter.id}>
                  <button
                    type="button"
                    className="group w-full px-3 py-2 text-left text-sm text-text hover:bg-muted flex items-center justify-between gap-3"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onChange(filter.name)}
                    data-testid={`${dataTestId}-suggestion-${filter.id}`}
                  >
                    <span className="truncate">{filter.name}</span>
                    <Check className="w-4 h-4 text-primary flex-shrink-0 opacity-0 group-hover:opacity-100" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-2 text-sm text-text-muted/80">
              {exactMatch
                ? t('shared.saveFilterset.existingNameUnavailable', 'That name already exists. Choose a different name.')
                : t('shared.saveFilterset.noMatchingNames', 'No matching saved filters. You can enter a new name.')}
            </div>
          )}
        </div>
      )}

    </div>
  );
};
